import React, { useState } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Chip,
	makeStyles,
	Box,
	Button,
	IconButton,
	Tooltip,
	Snackbar
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
	card: {
		marginBottom: theme.spacing(2),
	},
	pageAccordion: {
		marginBottom: theme.spacing(1),
	},
	pageUrl: {
		fontSize: '0.875rem',
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
	pageMetrics: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	metricChip: {
		fontSize: '0.75rem',
	},
	extractedText: {
		backgroundColor: theme.palette.grey[50],
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		fontSize: '0.875rem',
		lineHeight: 1.5,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	languageChip: {
		marginLeft: theme.spacing(1),
	},
	headerActions: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: theme.spacing(2),
	},
	actionButtons: {
		display: 'flex',
		gap: theme.spacing(1),
	},
	copyButton: {
		marginTop: theme.spacing(1),
	},
	extractedTextContainer: {
		position: 'relative',
	},
	copyIconButton: {
		position: 'absolute',
		top: 0,
		right: 0,
	},
	pageAiSection: {
		backgroundColor: theme.palette.grey[100],
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		marginBottom: theme.spacing(2),
		borderLeft: `4px solid ${theme.palette.primary.main}`,
	},
	pageAiSummary: {
		fontSize: '0.875rem',
		lineHeight: 1.5,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
	pageAiChips: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(1),
	},
	pageAiLabel: {
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(0.5),
	},
}));

const PagesAccordion = ({ pages }) => {
	const classes = useStyles();
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

	if (!pages || pages.length === 0) {
		return null;
	}

	// Format a single page as text with header
	const formatPageText = (page, index) => {
		const separator = '='.repeat(80);
		return `${separator}
			PAGE ${index + 1}: ${page.title || 'Untitled'}
			URL: ${page.url}
			${separator}

			${page.extractedText || 'No content available'}

			`;
	};

	// Format all pages as text
	const formatAllPagesText = () => {
		return pages.map((page, index) => formatPageText(page, index)).join('\n\n');
	};

	// Copy individual page to clipboard
	const copyPageToClipboard = async (page, index) => {
		try {
			const text = formatPageText(page, index);
			await navigator.clipboard.writeText(text);
			setSnackbar({ open: true, message: 'Page content copied to clipboard!', severity: 'success' });
		} catch (err) {
			console.error('Failed to copy:', err);
			setSnackbar({ open: true, message: 'Failed to copy to clipboard', severity: 'error' });
		}
	};

	// Copy all pages to clipboard
	const copyAllToClipboard = async () => {
		try {
			const text = formatAllPagesText();
			await navigator.clipboard.writeText(text);
			setSnackbar({ open: true, message: `All ${pages.length} pages copied to clipboard!`, severity: 'success' });
		} catch (err) {
			console.error('Failed to copy:', err);
			setSnackbar({ open: true, message: 'Failed to copy to clipboard', severity: 'error' });
		}
	};

	// Download all pages as text file
	const downloadAsTextFile = () => {
		try {
			const text = formatAllPagesText();
			const blob = new Blob([text], { type: 'text/plain' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			
			// Generate filename with timestamp
			const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
			link.download = `crawled-pages-${timestamp}.txt`;
			
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			
			setSnackbar({ open: true, message: 'Text file downloaded successfully!', severity: 'success' });
		} catch (err) {
			console.error('Failed to download:', err);
			setSnackbar({ open: true, message: 'Failed to download file', severity: 'error' });
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	return (
		<>
			<Card className={classes.card}>
				<CardContent>
					<div className={classes.headerActions}>
						<Typography variant="h6">
							Crawled Pages ({pages.length})
						</Typography>
						<div className={classes.actionButtons}>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								startIcon={<FileCopyIcon />}
								onClick={copyAllToClipboard}
							>
								Copy All
							</Button>
							<Button
								variant="outlined"
								color="secondary"
								size="small"
								startIcon={<GetAppIcon />}
								onClick={downloadAsTextFile}
							>
								Download as Text
							</Button>
						</div>
					</div>
					
					{pages.map((page, index) => (
					<Accordion key={index} className={classes.pageAccordion}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls={`panel${index}-content`}
							id={`panel${index}-header`}
						>
							<Box>
								<Typography variant="subtitle1">
									{page.title || `Page ${index + 1}`}
								</Typography>
								<Typography className={classes.pageUrl}>
									{page.url}
								</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Box width="100%">
								{/* Page Metrics */}
								<div className={classes.pageMetrics}>
									<Chip
										label={`Words: ${page.wordCount || 0}`}
										size="small"
										variant="outlined"
										className={classes.metricChip}
									/>
									<Chip
										label={`Unique Words: ${page.uniqueWordCount || 0}`}
										size="small"
										variant="outlined"
										className={classes.metricChip}
									/>
									<Chip
										label={`Characters: ${page.textLength || 0}`}
										size="small"
										variant="outlined"
										className={classes.metricChip}
									/>
									{page.language && (
										<Chip
											label={`Language: ${page.language}`}
											size="small"
											color="secondary"
											variant="outlined"
											className={classes.metricChip}
										/>
									)}
								</div>

								{/* Per-page AI Analysis (when present) */}
								{(page.aiAnalysis || page.overallAiAnalysis) && (() => {
									const pageAi = page.aiAnalysis || page.overallAiAnalysis;
									const summaryText = pageAi.briefSummary || pageAi.summary;
									return (
										<div className={classes.pageAiSection}>
											<Typography variant="subtitle2" gutterBottom>
												AI Analysis (this page)
											</Typography>
											{summaryText && (
												<Typography className={classes.pageAiSummary}>
													{summaryText}
												</Typography>
											)}
											{(pageAi.contentType || pageAi.detectedLanguage || pageAi.difficulty != null || pageAi.tone || pageAi.languageConfidence) && (
												<div className={classes.pageAiChips}>
													{pageAi.contentType && (
														<Chip label={pageAi.contentType} size="small" variant="outlined" />
													)}
													{pageAi.detectedLanguage && (
														<Chip label={`Language: ${pageAi.detectedLanguage}`} size="small" variant="outlined" />
													)}
													{pageAi.difficulty != null && (
														<Chip label={`Difficulty: ${pageAi.difficulty}/10`} size="small" variant="outlined" />
													)}
													{pageAi.tone && (
														<Chip label={`Tone: ${pageAi.tone}`} size="small" variant="outlined" />
													)}
													{pageAi.languageConfidence && (
														<Chip label={`Confidence: ${pageAi.languageConfidence}`} size="small" variant="outlined" />
													)}
												</div>
											)}
											{pageAi.keyTopics && pageAi.keyTopics.length > 0 && (
												<>
													<Typography className={classes.pageAiLabel}>Key topics</Typography>
													<div className={classes.pageAiChips}>
														{pageAi.keyTopics.map((topic, i) => (
															<Chip key={i} label={topic} size="small" color="primary" variant="outlined" />
														))}
													</div>
												</>
											)}
											{pageAi.specialElements && pageAi.specialElements.length > 0 && (
												<>
													<Typography className={classes.pageAiLabel}>Special elements</Typography>
													<div className={classes.pageAiChips}>
														{pageAi.specialElements.map((el, i) => (
															<Chip key={i} label={el} size="small" variant="outlined" />
														))}
													</div>
												</>
											)}
											{pageAi.requiredExpertise && pageAi.requiredExpertise.length > 0 && (
												<>
													<Typography className={classes.pageAiLabel}>Required expertise</Typography>
													<div className={classes.pageAiChips}>
														{pageAi.requiredExpertise.map((e, i) => (
															<Chip key={i} label={e} size="small" variant="outlined" />
														))}
													</div>
												</>
											)}
											{pageAi.keyChallenges && pageAi.keyChallenges.length > 0 && (
												<>
													<Typography className={classes.pageAiLabel}>Key challenges</Typography>
													<div className={classes.pageAiChips}>
														{pageAi.keyChallenges.map((c, i) => (
															<Chip key={i} label={c} size="small" variant="outlined" />
														))}
													</div>
												</>
											)}
										</div>
									);
								})()}

								{/* Extracted Text */}
								{page.extractedText && (
									<div className={classes.extractedTextContainer}>
										<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
											<Typography variant="subtitle2">
												Extracted Content
											</Typography>
											<Tooltip title="Copy this page content">
												<IconButton
													size="small"
													color="primary"
													onClick={() => copyPageToClipboard(page, index)}
												>
													<FileCopyIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</Box>
										<div className={classes.extractedText}>
											{page.extractedText}
										</div>
									</div>
								)}
							</Box>
						</AccordionDetails>
					</Accordion>
				))}
			</CardContent>
		</Card>
		
		<Snackbar
			open={snackbar.open}
			autoHideDuration={3000}
			onClose={handleCloseSnackbar}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
		>
			<Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
				{snackbar.message}
			</Alert>
		</Snackbar>
		</>
	);
};

export default PagesAccordion;
